import { Injectable } from '@angular/core';
import { DeficiencyTicketWatchersDaoService } from '../deficiency-ticket-watchers-dao.service';
import { DeficiencyTicketWatcher } from 'src/app/_model/deficiency-ticket-watcher';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { DeficiencyTicketWatchersDbTableDefinition } from './deficiency-ticket-watchers-db-table-definition';
import { LocalDeficiencyTicketWatchersDbTableDefinition } from './local-deficiency-ticket-watchers-db-table-definition';

@Injectable()
export class DeficiencyTicketWatchersDbDaoService implements DeficiencyTicketWatchersDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private deficiencyTicketWatchersTableDefinition: DeficiencyTicketWatchersDbTableDefinition,
    private localDeficiencyTicketWatchersTableDefinition: LocalDeficiencyTicketWatchersDbTableDefinition,
  ) {}

  public async saveDeficiencyTicketWatchers(entries: DeficiencyTicketWatcher[]): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const queries: Query[] = entries.map((entry) => ({
        statement:
          'INSERT OR REPLACE INTO ' +
          this.deficiencyTicketWatchersTableDefinition.tableName +
          '(id, watcherobjref, watcherid, deficiencyticketid, databag)' +
          ' VALUES (?,?,?,?,?)',
        parameters: [
          Number(entry.getImsId()),
          entry.watcherObjRef,
          entry.watcherId,
          entry.deficiencyTicketId,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      }));
      await this.dbPersistenceService.executeBatch(db, queries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving deficiency ticket watchers to db',
        error?.message,
      );
    }
  }

  public async createDeficiencyTicketWatcher(entry: DeficiencyTicketWatcher): Promise<DeficiencyTicketWatcher> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const query: Query = {
        statement:
          'INSERT INTO ' +
          this.localDeficiencyTicketWatchersTableDefinition.tableName +
          '(watcherobjref, watcherid, deficiencyticketid, databag)' +
          ' VALUES (?,?,?,?)',
        parameters: [
          entry.watcherObjRef,
          entry.watcherId,
          entry.deficiencyTicketId,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      };

      await this.dbPersistenceService.executeStatement(db, query);
      const res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        entry.id.secondaryId = res.values.shift()?.id;
      }
      return entry;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error creating local deficiency ticket watcher',
        error?.message,
      );
      return undefined;
    }
  }

  public async getDeficiencyTicketWatchers(): Promise<DeficiencyTicketWatcher[]> {
    const db = await this.dbPersistenceService.getImsDb();
    const entries: DeficiencyTicketWatcher[] = [];

    // Synced entries - isolated try/catch so a problem reading the local table
    // (e.g. not migrated yet) can never wipe out the already-loaded synced ones.
    try {
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.deficiencyTicketWatchersTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        const row = data.values[i];
        entries.push(
          DeficiencyTicketWatcher.fromDto(
            {
              id: row['id'],
              watcherobjref: row['watcherobjref'],
              watcherid: row['watcherid'],
              deficiencyticketid: row['deficiencyticketid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading synced deficiency ticket watchers from db',
        error?.message,
      );
    }

    // Locally created watchers - kept available until the backend syncs the
    // proper watcher back (see reconcile note in the logic service).
    try {
      const localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localDeficiencyTicketWatchersTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < localData.values.length; i++) {
        const row = localData.values[i];
        entries.push(
          DeficiencyTicketWatcher.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              watcherobjref: row['watcherobjref'],
              watcherid: row['watcherid'],
              deficiencyticketid: row['deficiencyticketid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading local deficiency ticket watchers from db',
        error?.message,
      );
    }

    return entries;
  }

  public async reset(): Promise<void> {
    // Unlike clear() (used during a sync), a Reset should wipe locally
    // created, not-yet-synced watchers too.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localDeficiencyTicketWatchersTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localDeficiencyTicketWatchersTableDefinition.tableName,
    );
  }

  public async clear(): Promise<void> {
    // Only the synced table is cleared here - locally created watchers must
    // survive a resync until the backend actually confirms/sends them back.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.deficiencyTicketWatchersTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.deficiencyTicketWatchersTableDefinition.tableName,
    );
  }
}
